import React, { useState } from "react";

import {
  FaUniversity,
  FaBookOpen,
  FaShoppingCart,
  FaPlus,
  FaMinus,
} from "react-icons/fa";

import { ButtonSecondary } from "../UI/Button";
import ZoomableImage from "./ZoomableImage";
import { openCalendly } from "./calendly";

import ProductLMSMindmap from "../../assets/product/productLMS-mindmap.png";
import ProductLMSUniversity from "../../assets/product/productLMS-university.png";
import ProductLMSODL from "../../assets/product/productLMS-odl.png";
import ProductLMSB2C from "../../assets/product/productLMS-b2c.png";

const insideItems = [
  {
    title: "Multitenancy architecture",
    text: "Run multiple colleges, departments or campuses on one platform, each with its own isolated environment.",
  },
  {
    title: "Multitenancy dashboard",
    text: "A single view for administrators to manage every tenant without switching systems.",
  },
  {
    title: "Admin workflow and dashboard",
    text: "Purpose-built tools for institutional administrators to manage courses, users and reporting.",
  },
  {
    title: "Faculty workflow and dashboard",
    text: "Content authoring, auto-grading and analytics designed around how faculty actually teach.",
  },
  {
    title: "Student workflow and dashboard",
    text: "Mobile-first access, digital certificates and progress tracking built for how students actually learn.",
  },
  {
    title: "Departments and course management",
    text: "Structure the platform around your real departmental hierarchy, and manage courses across departments and cohorts from one place.",
  },
];

const learningModels = [
  {
    label: "For Universities & Colleges",
    icon: FaUniversity,
    text: "Run your entire university on one platform, mapped exactly to your academic structure. Faculties, schools and departments, programmes and subjects each sit in their own place, so deans, heads of department and teachers see only what belongs to them. Admissions, attendance, assessments and results all roll up from the subject level to a university-wide view.",
    image: ProductLMSUniversity,
  },
  {
    label: "For Open & Distance Learning (ODL)",
    icon: FaBookOpen,
    text: "Built for the way open universities work, with learners spread across regions and study centres. Regional centres manage their own study centres, study centres support their learners, and every programme runs by admission batch. Study material, live and recorded counselling sessions, assignments and online exams reach learners wherever they are, and reports can be drawn per study centre, per programme or per batch.",
    image: ProductLMSODL,
  },
  {
    label: "For Course Sellers & EdTech Businesses",
    icon: FaShoppingCart,
    text: "Launch your own branded course platform, the way Coursera and Udemy work, but fully under your control. Organise courses into categories, onboard instructors and partner institutes to host their own content, and sell directly to learners through an integrated payment gateway. Learners enrol instantly, learn at their own pace and receive certificates automatically, while you track enrolments, revenue and course performance from one dashboard.",
    image: ProductLMSB2C,
  },
];

const audience = [
  {
    title: "Universities",
    text: "A white-label platform with NAAC-ready reporting built in, so accreditation documentation isn’t a separate project.",
  },
  {
    title: "Faculty",
    text: "Content authoring, auto-grading and analytics that reduce administrative load without reducing oversight.",
  },
  {
    title: "Students",
    text: "Mobile-first access, digital certificates and progress tracking that make the platform something students actually use.",
  },
];

const IntroCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 border-l-4 border-l-quaternary shadow-sm p-6 lg:p-8">
    <h2 className="text-2xl lg:text-3xl font-bold text-quaternary pb-5">
      {title}
    </h2>
    <div className="text-secondary leading-7 space-y-4">{children}</div>
  </div>
);

const FeatureCard = ({ title, text }) => (
  <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-quaternary shadow-sm p-6 lg:p-8 h-full">
    <h3 className="text-lg font-bold text-primary pb-4">{title}</h3>
    <p className="text-secondary leading-7">{text}</p>
  </div>
);

const SectionHeading = ({ children }) => (
  <h2 className="text-2xl lg:text-4xl font-bold text-quaternary text-center pb-8 lg:pb-10">
    {children}
  </h2>
);

const Content = () => {
  // null = all closed (only possible from the mobile accordion)
  const [activeModel, setActiveModel] = useState(0);
  const desktopModel = learningModels[activeModel ?? 0];

  return (
    <>
      <section className="container text-primary py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <IntroCard title="Built for Institution Scale from Day One">
            <p>
              We pride ourselves on being at the forefront of e-learning
              innovation. With expertise in Moodle LMS customization, we design
              and develop solutions that align seamlessly with the unique
              educational objectives and operational requirements of each
              institution.
            </p>
            <p>
              Our customized Moodle LMS solutions are designed for
              institution-scale deployment from day one, providing every
              stakeholder with dedicated workflows, role-based access, and
              dashboards tailored to their specific responsibilities and
              requirements.
            </p>
            <p>
              From learning and assessment to administration, monitoring,
              reporting, and student support, our LMS solutions create a
              structured, scalable, and engaging digital learning environment
              that supports the complete academic ecosystem.
            </p>
          </IntroCard>

          <IntroCard title="Built for Compliance, Not just Learning">
            <p>
              The platform is designed and configured considering NAAC
              guidelines and applicable Government of India standards and
              requirements, with compliance and reporting requirements
              integrated into the platform and workflows. This helps
              institutions manage documentation, reporting, and
              compliance-related information as a natural part of their
              everyday LMS operations.
            </p>
            <p>
              This approach helps universities maintain organized records,
              streamline reporting, and stay better prepared for audits,
              accreditation processes, and regulatory requirements.
            </p>
          </IntroCard>
        </div>
      </section>

      <section className="bg-gray-50 py-12 lg:py-16">
        <div className="container text-primary">
          <SectionHeading>What's inside</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insideItems.map((item) => (
              <FeatureCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </section>

      <section className="container text-primary py-12 lg:py-16">
        <SectionHeading>
          Functionalities of our AI-Powered Learning Management System
        </SectionHeading>
        <div className="mx-auto w-full lg:w-[80%]">
          <ZoomableImage
            src={ProductLMSMindmap}
            alt="Functionalities of our AI-Powered Learning Management System"
            />
        </div>
      </section>

      <section className="bg-gray-50 py-12 lg:py-16">
        <div className="container text-primary">
          <SectionHeading>
            One Platform<span className="hidden lg:inline">, </span>
            <br className="lg:hidden" />
            Every Learning Model
          </SectionHeading>

          {/* Desktop: tabs */}
          <div className="hidden lg:block">
            <div className="flex justify-center gap-3 pb-8">
              {learningModels.map((model, index) => {
                const Icon = model.icon;
                const isActive = desktopModel === model;
                return (
                  <button
                    key={model.label}
                    onClick={() => setActiveModel(index)}
                    className={`flex items-center gap-2 px-8 py-5 font-semibold border-2 border-quaternary transition-all duration-300 ${
                      isActive
                        ? "bg-quaternary text-tertiary"
                        : "bg-white text-quaternary hover:bg-quaternary hover:text-tertiary"
                    }`}
                  >
                    <Icon /> {model.label}
                  </button>
                );
              })}
            </div>
            <p className="text-secondary leading-7 pb-8">{desktopModel.text}</p>
            <ZoomableImage
              src={desktopModel.image}
              alt={desktopModel.label}
              className="border-4 border-primary"
              />
          </div>

          {/* Mobile: accordion */}
          <div className="lg:hidden space-y-3">
            {learningModels.map((model, index) => {
              const Icon = model.icon;
              const isOpen = activeModel === index;
              return (
                <div
                  key={model.label}
                  className="rounded-xl border border-quaternary/40 overflow-hidden bg-white"
                >
                  <button
                    onClick={() => setActiveModel(isOpen ? null : index)}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-4 text-left font-semibold transition-all duration-300 ${
                      isOpen
                        ? "bg-quaternary text-tertiary"
                        : "bg-white text-quaternary"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="shrink-0" /> {model.label}
                    </span>
                    {isOpen ? (
                      <FaMinus className="shrink-0" />
                    ) : (
                      <FaPlus className="shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="p-4">
                      <p className="text-secondary leading-7 pb-4">
                        {model.text}
                      </p>
                      <ZoomableImage
                        src={model.image}
                        alt={model.label}
                        className="border-2 border-primary"
                        />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container text-primary py-12 lg:py-16">
        <SectionHeading>Who it's for</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {audience.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#0b2b4f] to-quaternary py-16 lg:py-20">
        <div className="container text-center">
          <h2 className="text-2xl lg:text-4xl font-bold text-tertiary pb-8">
            See the LMS in action
          </h2>
          <ButtonSecondary onClick={openCalendly}>Book a demo</ButtonSecondary>
        </div>
      </section>
    </>
  );
};

export default Content;
